import { TeamStore } from 'nomst/Stores/team.store';
import { RouterStore, syncHistoryWithStore } from 'nomst/Stores/Navigation/router.store';
import { History, Location } from 'history';
import { DecisionNavigationStore } from 'nomst/Stores/Navigation/decisionNavigation.store';
import * as decisionUrlBuilder from 'nomst/Stores/Navigation/decisionUrlBuilder';
import {
    WorkflowStatusListType,
    ReportsView,
    SidePanelListType,
    TeamRoutes,
    ConfigTabs,
    ConfigViews,
    EligibilityProrationTabs, 
    PageTabs } from 'typings/enums';
import {
    changeHistoryStore,
    managerListSidePanelStore,
    workflowSettingsViewStore,
    contactFormHelper,
    employeeList,
    compMemoStore,
    jobStore } from 'utils/MobxDataManager/singletons';
import {
    managerIdParameterName,
    cycleIdParameterName,
    workflowStatusParameterName,
    viewParameterName,
    currentBudgetParameter,
    workflowViewParamater,
    rulesTabParamater,
    rulesPanelParamater,
    compMemoEmployeeIdParameterName
} from 'nomst/Stores/Navigation/Constants/decisionPageUrl';
import * as formatParse from 'common/format-parse';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { DecisionStore } from 'nomst/Stores/Decision/decision.store';
import * as actions from 'nomst/Actions/configurations';
import { ICycleNavigation } from 'typings/cycleNavigation';
import * as createRouteConfig from 'path-match';
import { RegExpOptions } from 'path-to-regexp';
import { routeFn } from 'path-match';
import { IEmployeeViewNavigationParameters } from 'typings/navigation';

// tslint:disable-next-line:no-any we need to do this to get around the lack of typings for path-match
const castedCreateRoute = createRouteConfig as any as (options?: RegExpOptions) => routeFn;
const route = castedCreateRoute({});
const noRoute = route('/');

export class NavigationStore {
    decisionNavigationStore: DecisionNavigationStore;
    private _navigationHistory: History;
    private readonly _routerStore: RouterStore;
    private _configuration: Configuration;
    private _decisionStore: DecisionStore;
    private _employeeId: Nullable<string>;
    private _isUserHrOrSupport: boolean;
    private _setPageTab: (tab: PageTabs) => void;

    constructor(teamStore: TeamStore, history: History, setPageTab: (tab: PageTabs) => void) {
        this._routerStore = new RouterStore();

        const { decisionStore, configuration } = teamStore;
        this._configuration = configuration;
        this._employeeId = teamStore?.user?.employeeSourceId;
        this._isUserHrOrSupport = teamStore?.user?.isHrOrSupport;
        this._setPageTab = setPageTab;

        this._decisionStore = decisionStore;
        this.decisionNavigationStore = new DecisionNavigationStore(
            this,
            decisionStore.decisionDispatcher,
            configuration,
            setPageTab);
    }

    updateNavigationStore(
        decisionStore: DecisionStore, configuration: Configuration, setPageTab: (tab: PageTabs) => void): void {
        this._configuration = configuration;
        this._decisionStore = decisionStore;
        this.decisionNavigationStore = new DecisionNavigationStore(
            this,
            decisionStore.decisionDispatcher,
            configuration,
            setPageTab);
    }

    navigateToOrgChartPage(managerSourceId: string): void {
        const orgChartRoute = decisionUrlBuilder.getOrgChartRoute(managerSourceId);
        this.navigate(orgChartRoute);
    }

    navigateToChangeHistory(
        cycleId: number,
        managerSourceId: string,
        workflowStatus: Nullable<WorkflowStatusListType>,
        reports: Nullable<ReportsView> = ReportsView.DirectsOnly): void {

        const status = workflowStatus ? workflowStatus : this.getLastWorkflowStatusListTypes();
        const reportsView = reports ? reports : this.getLastReportsView();

        const changeHistoryRoute = decisionUrlBuilder
            .getChangeHistoryRoute(cycleId, managerSourceId, status, reportsView);
        this.navigate(changeHistoryRoute);
    }

    navigateToOverBudgetManagersRoute(
        cycleId: number,
        managerSourceIds: string[],
        budgetCycleId: number,
        reports: ReportsView = ReportsView.DirectsOnly,
        showSidePanel: boolean = true): void {
        this._decisionStore.teamDecisionStore.handleDispatchPromotionOnlyChanged(false);
        
        const managerListRoute = decisionUrlBuilder.getOverBudgetManagersRoute(
            this.createQuickNavigationParams(cycleId, managerSourceIds, WorkflowStatusListType.Open, reports, showSidePanel),
            budgetCycleId
        );

        this.navigate(managerListRoute);
    }

    navigateToDirectTeamsView(cycleId: Nullable<number>, managerSourceIds: string[], promotionRequest?: boolean): void {
        const route = decisionUrlBuilder.getWorkflowManagersRoute(
            this.createQuickNavigationParams(
                cycleId ?? this._configuration.cycleId,
                managerSourceIds,
                this.getLastWorkflowStatusListTypes(),
                ReportsView.DirectsOnly,
                false)
        );
        this._decisionStore.teamDecisionStore.handleDispatchPromotionOnlyChanged(!!promotionRequest);
        this.navigate(route);
    }

    navigateToAllReportsTeamsView(cycleId: Nullable<number>, managerSourceIds: string[], promotionRequest?: boolean): void {
        const route = decisionUrlBuilder.getWorkflowManagersRoute(
            this.createQuickNavigationParams(
                cycleId ?? this._configuration.cycleId,
                managerSourceIds,
                this.getLastWorkflowStatusListTypes(),
                ReportsView.AllReports,
                false)
        );
        this._decisionStore.teamDecisionStore.handleDispatchPromotionOnlyChanged(!!promotionRequest);
        this.navigate(route);
    }

    navigateToTeamsView(): void {
        const route = decisionUrlBuilder.getWorkflowManagersRoute(
            this.createQuickNavigationParams(
                this._configuration.cycleId,
                [],
                this.getLastWorkflowStatusListTypes(),
                ReportsView.DirectsOnly,
                true)
        );
        this._decisionStore.teamDecisionStore.handleDispatchPromotionOnlyChanged(false);
        this.navigate(route);
    }

    createQuickNavigationParams(
        cyleId: number,
        managerIds: string[],
        workflowStatus: WorkflowStatusListType,
        reportsView: ReportsView,
        showSidePanel: boolean
    ): IEmployeeViewNavigationParameters {
        return {
            CycleId: cyleId,
            ManagerSourceIds: managerIds,
            WorkflowStatus: workflowStatus,
            ReportsView: reportsView,
            ShowSidePanel: showSidePanel,
            SortBy: this._decisionStore.teamDecisionStore.employeeSort,
            PromotionFilter: this._decisionStore.teamDecisionStore.showPromotionsOnly,
            JobTitleFilter: this._decisionStore.teamDecisionStore.filterByJob,
            DepartmentFilter: this._decisionStore.teamDecisionStore.filterByDepartment,
            SelectedEmployee: null, // keeping null because employee will not likely be in new view
            QueryParameters: this.decisionNavigationStore.queryParameters

        };
    }

    navigateToWorkflowStatusManagersRoute(
        cycleId: number,
        managerSourceIds: string[],
        workflowStatus: Nullable<WorkflowStatusListType>,
        reports: Nullable<ReportsView> = ReportsView.DirectsOnly,
        showSidePanel: boolean = true): void {

        const status = workflowStatus ? workflowStatus : this.getLastWorkflowStatusListTypes();
        const reportsView = reports ? reports : this.getLastReportsView();

        // make sure there's no (over budget) budget cycle id hanging around if we're going to workflow status
        managerListSidePanelStore.resetBudgetCycleId();

        this._decisionStore.teamDecisionStore.handleDispatchPromotionOnlyChanged(false);
        const managerListRoute = decisionUrlBuilder.getWorkflowManagersRoute({
            CycleId: cycleId,
            ManagerSourceIds: managerSourceIds,
            WorkflowStatus: status,
            ReportsView: reportsView,
            ShowSidePanel: showSidePanel,
            SortBy: this._decisionStore.teamDecisionStore.employeeSort,
            PromotionFilter: this._decisionStore.teamDecisionStore.showPromotionsOnly,
            JobTitleFilter: this._decisionStore.teamDecisionStore.filterByJob,
            DepartmentFilter: this._decisionStore.teamDecisionStore.filterByDepartment,
            SelectedEmployee: null, // keeping null because employee will not likely be in new view
            QueryParameters: this.decisionNavigationStore.queryParameters
        });

        this.navigate(managerListRoute);
    }

    navigateToUpdateState(navParams: IEmployeeViewNavigationParameters): void {
        const currentVariables = this.decisionNavigationStore.getAllParameters(this._navigationHistory.location.search);
        Object.keys(navParams).forEach((key: keyof IEmployeeViewNavigationParameters) => {
            currentVariables[key] = navParams[key] as never;
        });
        this.decisionNavigationStore.setShouldFullyNavigate(false);
        const managerListRoute = decisionUrlBuilder.getWorkflowManagersRoute(currentVariables);
        this.navigate(managerListRoute);
    }

    async navigateToCycleSettings(cycleId: number): Promise<void> {
        const cycleSettingsRoute = decisionUrlBuilder.getCycleSettingsRoute(cycleId);
        this.navigate(cycleSettingsRoute);
    }

    navigateToMstWorkflowSettings(cycleId: number): void {
        const workflowSettingsRoute = decisionUrlBuilder.getMstWorkflowSettingsRoute(cycleId);
        this.navigate(workflowSettingsRoute);
    }

    navigateToMstBudgetSettings(cycleId: number): void {
        const budgetSettingsRoute = decisionUrlBuilder.getMstBudgetSettingsRoute(cycleId);
        this.navigate(budgetSettingsRoute);
    }

    navigateToEmptyManagerView(): void {
        const emptyManagerViewRoute = decisionUrlBuilder.getEmptyManagerViewRoute();
        this.navigate(emptyManagerViewRoute);
    }

    navigateToErrorMessageView(): void {
        const errorMessageViewRoute = decisionUrlBuilder.getErrorMessageViewRoute();
        this.navigate(errorMessageViewRoute);
    }

    navigateToDiagnosticsView(): void {
        const diagnosticsRoute = decisionUrlBuilder.getDiagnosticsRoute();
        this.navigate(diagnosticsRoute);
    }

    navigateToEmployee(
        cycleId: number,
        managerSourceId: NullOrUndefined<string>,
        workflowStatus: Nullable<WorkflowStatusListType>,
        employeeSourceId: NullOrUndefined<string>,
        reports: Nullable<ReportsView> = ReportsView.DirectsOnly): void {

        const status = workflowStatus ? workflowStatus : this.getLastWorkflowStatusListTypes();
        const reportsView = reports ? reports : this.getLastReportsView();
        const navParams = this.createQuickNavigationParams(
            cycleId,
            [managerSourceId ?? ''],
            status,
            reportsView,
            true
        );

        if (managerSourceId) {
            const managerListRoute = decisionUrlBuilder.getSelectedEmployeeDetailsRoute(navParams, employeeSourceId);
            if (managerListRoute) {
                this.navigate(managerListRoute);
            }
        }
    }

    isInPdfRoute(): boolean {
        return this._navigationHistory?.location.pathname.includes(TeamRoutes.Print);
    }

    handleReportsViewNavigation(
        cycleId: number,
        managerSourceIds: string[],
        reports: ReportsView,
        workflowStatus: Nullable<WorkflowStatusListType>,
        budgetCycleId: Nullable<number>
    ): void {
        const showSidePanel = managerListSidePanelStore.sidePanelIsOpen;
        if (workflowStatus) {
            this.navigateToWorkflowStatusManagersRoute(
                cycleId,
                managerSourceIds,
                workflowStatus,
                reports,
                showSidePanel);
        } else if (budgetCycleId) {
            this.navigateToOverBudgetManagersRoute(
                cycleId,
                managerSourceIds,
                budgetCycleId,
                reports,
                showSidePanel);
        } else {
            this.navigateToWorkflowStatusManagersRoute(
                cycleId,
                managerSourceIds,
                null,
                reports,
                showSidePanel);
        }
    }

    goToAccessDenied(): void {
        return this.navigate(decisionUrlBuilder.getAccessDeniedRoute());
    }

    goToConfigurationView(tab: ConfigTabs): void {
        return this.navigate(decisionUrlBuilder.getConfigurationRoute(tab));
    }

    navigateCycleConfigurationView(nav: ICycleNavigation): void {
        return this.navigate(decisionUrlBuilder.getCycleConfigurationRoute(nav));
    }

    goToOverview(cycleId: number): void {
        this.navigate(`${TeamRoutes.SummaryDashboard}?cycleId=${cycleId}`);
    }

    goToJobs(): void {
        this.navigate(TeamRoutes.JobsList);
    }

    goToJobDetailsView(jobCode: string, jobId: string | number): void {
        this._navigationHistory.push(`${TeamRoutes.JobDetails}?jobCode=${encodeURIComponent(jobCode)}&jobId=${encodeURIComponent(jobId.toString())}`);
    }

    goToEmployees(): void {
        this.navigate(TeamRoutes.EmployeesList);
    }

    goToEmployeesDetailsView(employeeSourceId: string): void {
        this._navigationHistory.push(`${TeamRoutes.EmployeeDetailsView}?employeeSourceId=${employeeSourceId}`);
    }

    goToCycleConfiguration(cycleId: number, isCycleClosedForId: boolean): void {
        isCycleClosedForId ?
            this.navigateToCycleSettings(cycleId) :
            this.navigate(
                `${TeamRoutes.CycleConfiguration}?cycleId=${cycleId}&view=${ConfigViews.Budget}`);
    }

    goToGlobalConfiguration(): void {
        this._navigationHistory.push(`${TeamRoutes.GlobalConfiguration}?view=${ConfigTabs.HRBP}`);
    }

    goToTeams(employeeSourceId: string, cycleId: number): void {
        this.navigateToDirectTeamsView(cycleId, [employeeSourceId]);
    }

    goToTeamsFromMx(): void {
        this.navigateToDirectTeamsView(this._configuration.cycleId, [this._employeeId ?? '']);
    }

    noRoute(): boolean {
        return noRoute(this._navigationHistory.location.pathname) === false;
    }

    async syncHistoryWithRouterStore(history: History): Promise<void> {
        syncHistoryWithStore(history, this._routerStore).subscribe();
        this._navigationHistory = history;
        await this.subscribeToHistory(history);
    }

    async goToCeoTeamView(promotionRequest?: boolean): Promise<void> {
        if (!this._decisionStore.topEmployeeId) {
            await this._decisionStore.fetchTopEmployeeId();
        }
        if (this._decisionStore.topEmployeeId) {
            promotionRequest ?
                this.navigateToAllReportsTeamsView(this._decisionStore.cycleId, [this._decisionStore.topEmployeeId], true) :
                this.navigateToDirectTeamsView(this._decisionStore.cycleId, [this._decisionStore.topEmployeeId]);
        }
    }

    async goToPromotionsView(): Promise<void> {
        if (this._isUserHrOrSupport) {
            return this.goToCeoTeamView(true);
        }

        const managerIds = this._employeeId ? [this._employeeId] : [];
        this.navigateToAllReportsTeamsView(this._decisionStore.cycleId, managerIds, true);
    }

    async testLocationChange(location: Location): Promise<void> {
        await this.handleLocationChange(location);
    }

    testHistoryObject(): History {
        return this._navigationHistory;
    }

    private navigate(url: string): void {
        this._navigationHistory.push(url);
    }

    private async subscribeToHistory(history: History): Promise<void> {
        this.handleLocationChange = this.handleLocationChange.bind(this);
        history.listen(this.handleLocationChange);
        await this.handleLocationChange(history.location);
    }

    private async handleLocationChange(location: Location): Promise<void> {
        const queryParameters = formatParse.getJsonFromUrl(location.search);
        const cycleIdValue = queryParameters[cycleIdParameterName];
        if (location.pathname.includes(TeamRoutes.Decision)) {
            await this.decisionNavigationStore.handleDecisionRoutes();
        }

        if (location.pathname.includes(TeamRoutes.CycleSettings) ||
            location.pathname.includes(TeamRoutes.CycleConfiguration)) {
            await Promise.all([actions.fetchCycleConfigurations(parseInt(cycleIdValue[0], 10)),
                workflowSettingsViewStore.initDataByCycleId(parseInt(cycleIdValue[0], 10))]);
        }

        if (location.pathname.includes(TeamRoutes.EmployeesList)) {
            this._setPageTab(PageTabs.Employees);
            await employeeList.getEmployeeList();
        }

        if (location.pathname.includes(TeamRoutes.JobsList)) {
            this._setPageTab(PageTabs.Jobs);
            await jobStore.fetchJobs();
        }

        if (location.pathname.includes(TeamRoutes.EmployeeDetailsView)) {
            this._setPageTab(PageTabs.Employees);
            const employeeSourceId = queryParameters['employeeSourceId'][0];
            await employeeList.setSelectedEmployeeSourceId(employeeSourceId);
        }

        if (location.pathname.includes(TeamRoutes.JobDetails)) {
            this._setPageTab(PageTabs.Jobs);
            const jobId = queryParameters['jobId'][0];
            const jobCode = queryParameters['jobCode'][0];
            jobStore.setSelectedJobId(jobId);
            jobStore.setSelectedJobCode(jobCode);
            await Promise.all([
                jobStore.fetchJobDetails(),
                jobStore.fetchJobLocations()
            ]);
        }

        if (location.pathname.includes(TeamRoutes.ChangeHistory)) {
            const managerId = queryParameters[managerIdParameterName];
            if (!managerId || managerId.length === 0) {
                return;
            }

            const workflowStatusType = (queryParameters[workflowStatusParameterName])[0] as WorkflowStatusListType;

            await changeHistoryStore.setManagerId(managerId[0]);
            const { masterAccountId } = this._configuration;

            await changeHistoryStore.fetchChangeHistoryData(masterAccountId, parseInt(cycleIdValue[0], 10));
            // tslint:disable-next-line:max-line-length
            await managerListSidePanelStore.setWorkflowManagerList(SidePanelListType.WorkflowStatus, workflowStatusType);
        }
        if (location.pathname.includes(TeamRoutes.HelpArticles)) {
            contactFormHelper.setFullPageHelpForm();
        }
        if (location.pathname.includes(TeamRoutes.GlobalConfiguration)) {
            const tab = formatParse.getFirstOnly(queryParameters[viewParameterName]) as ConfigTabs;
            actions.setConfigurationTab(tab);
        }
        if (location.pathname.includes(TeamRoutes.CycleConfiguration)) {
            const view = formatParse.getFirstOnly(queryParameters[viewParameterName]) as ConfigViews;
            const cycleId = Number(formatParse.getFirstOnly(queryParameters[cycleIdParameterName]));
            const amountCode = formatParse.getFirstOnly(queryParameters[currentBudgetParameter]);
            const workflowView = formatParse.getFirstOnly(queryParameters[workflowViewParamater]);
            const rulesPanel = Boolean(formatParse.getFirstOnly(queryParameters[rulesPanelParamater]));
            const rulesTab = formatParse.getFirstOnly(queryParameters[rulesTabParamater]) as EligibilityProrationTabs;
            
            await actions.setupCycleConfigurationView({
                cycleId,
                view,
                amountCode,
                workflowView,
                rulesPanel,
                rulesTab
            } as ICycleNavigation);
        }
        if (location.pathname.includes(TeamRoutes.Print)) {
            // TODO (COLAB-1423): This is a temporary solution until we add the button for generating the comp memos.
            
            const employeeSourceId = formatParse.getFirstOnly(queryParameters[compMemoEmployeeIdParameterName]);
            if (!employeeSourceId?.length) {
                return;
            }

            await compMemoStore.getEmployeeCompMemoData(employeeSourceId);
        }
        contactFormHelper?.setSearchTerms(location.pathname, location.search);
        contactFormHelper?.setCurrentPage(`${location.pathname}${location.search}`);

    }

    private getLastWorkflowStatusListTypes(): WorkflowStatusListType {
        return managerListSidePanelStore.workflowStatusType;
    }

    private getLastReportsView(): ReportsView {
        return this._decisionStore.teamDecisionStore.teamView;
    }
}
export interface INavigationStore extends NavigationStore {}